import {
  Component, OnDestroy, OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UpdateCourseComponent } from './components/update-course/update-course.component';
import { CoursesService } from 'src/app/services/courses.service';
import { CourseFiltersComponent } from './components/course-filters/course-filters.component';
import { CatalogCourseDetailsComponent } from './components/catalog-course-details/catalog-course-details.component';
import { RegistrationRequirementModalComponent } from './components/registration-requirement-modal/registration-requirement-modal.component';
import { RegistrationWaitlistModalComponent } from './components/registration-waitlist-modal/registration-waitlist-modal.component';
import { EntityService } from 'src/app/services/entity.service';
import { UsersService } from 'src/app/services/users.service';
import { AuthService } from 'src/app/services/auth.service';
import { CourseFilter } from './components/course-filters/courseFilter';
import {
  NewTableData, TableBurgerClickEvent, TablePageChangeEvent, TableRow, TableRowClickEvent,
  TableSortEvent,
} from 'src/app/components/new-table/new-table.component';
import { formatDate } from 'src/app/utils';
import { GeneralDataCell, IconCell } from 'src/app/components/new-table/table-cell/table-cell.component';
import { PERMISSION_KIDS, USER_PERMISSIONS_OBJECT } from 'src/app/constants/permissionKids';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';
import { ToastService } from 'src/app/services/toast.service';
import { Subscription } from 'rxjs';
import { VerificationComponent } from 'src/app/components/verification/verification.component';

@Component({
  selector: 'app-course-catalog',
  templateUrl: './course-catalog.component.html',
  styleUrls: ['./course-catalog.component.scss'],
})
export class CourseCatalogComponent implements OnInit, OnDestroy {
  meatballMenu: any;
  years: any;
  formattedYears: any;
  isLoading = false;
  error: any;
  rawCourseData: any;
  courseList: any;
  totalCount: any;
  currentFilters: any;
  user: any;
  permissions: any;
  mainFilter: CourseFilter;
  filterString = '';
  sortColumn = 'dateOffered';
  sortDirection: 'ASC' | 'DESC' = 'ASC';
  yearId: number;
  curYearId: number;

  currentRequestId: string;

  loadingSpinnerIsLoading = true;

  userCurrentRegistrationIds: number[];
  userRegistrations: any;
  userCanCreate = false;
  userCanEdit = false;
  limit = 25;
  offset = 0;

  selectedYear: number;

  hasPrivilege = false;
  privilegeKIDs = [
    PERMISSION_KIDS.COURSE_ADD, PERMISSION_KIDS.COURSE_EDIT, PERMISSION_KIDS.COURSE_PUSH,
  ];

  // #region Subscription Declarations
  coursesDataSubscription: Subscription;
  exportCoursesDataSubscription: Subscription;
  entitySubscription: Subscription;
  permissionSubscription: Subscription;
  userSubscription: Subscription;
  userWithRequirementsSubscription: Subscription;
  loadingSpinnerSubscription: Subscription;

  // #endregion Subscription Declarations


  // #region new Table Data
  tableData: NewTableData = {
    title: 'Course Catalog',
    columnTitles: [
      {
        name: 'Course',
        sortable: true,
        sortByProp: 'courseName',
        width: '300px',
      },
      {
        name: 'Location',
        sortable: false,
      },
      {
        name: 'Date & Time',
        sortable: true,
        sortByProp: 'dateOffered',
        sortDirection: 'ASC',
      },
      {
        name: 'Hours',
        sortable: true,
        sortByProp: 'trackingValue',
      },
      {
        name: 'Enrollment',
        sortable: true,
        sortByProp: 'registeredCount',
      },
      { name: '' },
    ],
    displayNoDataMessage: false,
    noDataFoundMessage: 'No courses match the current filters',
    rowStyling: 'default',
    burgerContent: [
      {
        content: 'View Details',
        eventName: 'viewDetails',
      },
      {
        content: 'Register',
        eventName: 'register',
        displayCondition: (itemId: number) => {
          const isCancelled = this.checkCourseCancelled(itemId);
          if (isCancelled) return false;

          const isRegistered = this.checkForRegistration(itemId);
          if (isRegistered) return false;

          const { canWaitlist, classFull } = this.confirmCourseStatus(itemId);
          return !canWaitlist && !classFull;
        },
      },
      {
        content: 'Join Waitlist',
        eventName: 'joinWaitlist',
        displayCondition: (itemId: number) => {
          const isCancelled = this.checkCourseCancelled(itemId);
          if (isCancelled) return false;

          const isRegistered = this.checkForRegistration(itemId);
          if (isRegistered) return false;

          const { canWaitlist } = this.confirmCourseStatus(itemId);

          return canWaitlist;
        },
      },
      {
        content: 'Edit Course',
        eventName: 'editCourse',
        displayCondition: () => this.confirmPermissions(),
      },
      {
        content: 'Cancel Course',
        eventName: 'cancelCourse',
        displayCondition: (itemId: number) => this.confirmPermissions() && !this.courseIsCancelled(itemId),
      },
    ],
    hasBurgerMenu: true,
    meta: {
      curPage: 1,
      itemsPerPage: 25,
      totalItems: 0,
      totalPages: 0,
      usePagination: true,
      links: {
        prev: '',
        next: '',
        first: '',
        last: '',
        self: '',
      },
    },
    data: [],
  }

  // #endregion new Table Data

  // Export loading state
  isExporting = false;

  // Export restriction based on record count
  isExportDisabled = false;

  constructor(
    public dialog: MatDialog,
    private coursesService: CoursesService,
    private entityService: EntityService,
    private authService: AuthService,
    private usersService: UsersService,
    private loadingSpinnerService: LoadingSpinnerService,
    private toastService: ToastService,
  ) { }

  //TODO: add pagination to the list of courses
  //TODO: update ui

  ngOnInit(): void {
    this.isLoading = true;
    this.mainFilter = new CourseFilter();
    this.entityService.getEntity('Years');
    this.formatFilter(this.mainFilter);

    // #region Subscription Handling
    this.coursesDataSubscription = this.coursesService.coursesData$.subscribe((data: any) => {
      if (!data || this.currentRequestId !== data.requestId) return;

      this.rawCourseData = data;
      this.loadingSpinnerService.setIsLoading(false);
      this.isLoading = false;

      this.processCourseData();
      this.currentRequestId = '';
    })



    this.entitySubscription = this.entityService.entityData$.subscribe((entity: any) => {
      if (!entity) return;
      // format years for reusable select
      this.curYearId = entity?.gcCurrentYearId;
      if (entity?.Years?.length > 0) {
        this.years = entity?.Years.reverse();
        this.formattedYears = entity?.Years?.map((year: any) => {
          return {
            text: year.years,
            value: year.id,
          }
        })

        this.selectedYear = this.formattedYears[0].value;

      }
    })

    this.permissionSubscription = this.authService.permissions$.subscribe((permissions: USER_PERMISSIONS_OBJECT) => {
      if (!permissions) return;
      this.permissions = permissions;

      this.hasPrivilege = this.privilegeKIDs.some((privilege) => permissions[privilege] === 1);

      this.userCanCreate = permissions.COURSE_ADD === 1;
      this.userCanEdit = permissions.COURSE_EDIT === 1;

      if (this.hasPrivilege) {
        // TODO: CHANGE BACK TO FALSE
        this.mainFilter.showUserViewFlag = false;
      }

      this.searchCourses();
    })

    this.userSubscription = this.authService.user$.subscribe((user: any) => {
      if (user) {
        this.user = user;
        this.searchForUserRegistrations();
      }
    })

    this.userWithRequirementsSubscription = this.usersService.selectedUserData$.subscribe((data: any) => {
      if (data) {
        this.user = data;
        this.userCurrentRegistrationIds = data?.Registrations?.map((registration: any) => registration.courseItemId);
        this.userRegistrations = data?.Registrations;
      }
    })

    this.loadingSpinnerSubscription = this.loadingSpinnerService.isLoading$.subscribe((isLoading: boolean) => {
      this.loadingSpinnerIsLoading = isLoading;
    })

    // #endregion Subscription Handling
  }

  searchForUserRegistrations = () => {
    const user = this.user;
    this.usersService.getUserById(user.id, { includedAssociations: 'Requirements, Registrations' });
  }

  processCourseData() {
    const data = this.rawCourseData;
    this.tableData.displayNoDataMessage = data?.rows?.length === 0;

    this.tableData.data = this.formatCourses(data);
    this.tableData.meta = this.formatMeta(data);
    data?.error ? this.error = data?.error : this.error = null;

    // Check if export should be disabled (more than 500 records)
    const totalRecords = data?.pagination?.totalItems || 0;
    this.isExportDisabled = totalRecords > 500;
  }

  searchCourses = (event?: any) => {
    // TODO: Course Catalog Filter by string endpoint really slow and needs to be optimized
    this.isLoading = true;
    this.tableData.data = [];

    const args = this.getCourseCallFilters(false);

    this.coursesService.getCourseSessions(args)
  }

  // #region Display Functions
  checkDisplayYearPermissions = () => {
    const permissions = this.permissions;

    const allowsPrivilege = [
      PERMISSION_KIDS.COURSE_ADD, PERMISSION_KIDS.COURSE_EDIT, PERMISSION_KIDS.COURSE_PUSH,
    ];

    const hasPrivilege = allowsPrivilege.some((privilege) => permissions[privilege] === 1);

    return hasPrivilege;
  }
  // #endregion Display Functions

  // #region Format Courses
  generateLocationCell(course: any): GeneralDataCell {
    const isVirtual = course.virtualFlag;

    if (isVirtual) {
      return {
        type: 'general',
        mainText: 'Virtual',
        subText: '-',
      }
    }

    return {
      type: 'general',
      mainText: course.location || '-',
      subText: course.room || '-',
    }
  }

  generateDateCell(course: any): GeneralDataCell {
    const type = course?.preRecordedOption;
    const isAsync = type === 'ASYNC';
    const isAnytime = type === 'ANYTIME';

    if (isAsync) {
      return {
        type: 'general',
        mainText: 'Async',
        subText: 'Async',
      }
    }

    if (isAnytime) {
      return {
        type: 'general',
        mainText: formatDate.asDate(course?.dateOffered) || '-',
        subText: 'Anytime',
      }
    }

    const dateCell: GeneralDataCell = {
      type: 'general',
      mainText: formatDate.asDate(course.dateOffered) || '-',
      subText: `${formatDate.asTime(course.sessionStartDate)} - ${formatDate.asTime(course.sessionEndDate)}`,
    };

    return dateCell;
  }

  formatSingleCourse = (course: any) => {
    const courseCell: GeneralDataCell = {
      type: 'general',
      mainText: course.courseName || '-',
      subText: course.courseItemId || '-',
    };
    const locationCell = this.generateLocationCell(course);

    const dateCell = this.generateDateCell(course);
    const hoursCell: GeneralDataCell = {
      type: 'general',
      mainText: course.trackingValue || '0',
    };

    const enrollmentCount = course.registeredCount || '0';
    const maxEnrollment = course.courseSize || '0';

    const enrollmentCell: GeneralDataCell = {
      type: 'general',
      mainText: `${enrollmentCount} / ${maxEnrollment}`,
      mainFontStyle: 'normal',
    };


    const { canWaitlist, classFull } = this.confirmCourseStatus(course.courseItemId);
    const iconCell: IconCell = {
      type: 'icon',
      icon: 'timelapse',
      display: canWaitlist && classFull,
      helperText: 'Waitlist Open',
      color: '#C2731C',
    }

    const { rowColor, rowStatus } = this.decideRowColor(course);

    const fullRowData: TableRow = {
      itemId: course.courseItemId,
      rowColor: rowColor,
      rowTooltip: rowStatus,
      columnData: [
        courseCell,
        locationCell,
        dateCell,
        hoursCell,
        enrollmentCell,
        iconCell,
      ],
    }

    return fullRowData;
  }

  decideRowColor = (course: any): {
    rowColor: TableRow['rowColor'],
    rowStatus: string;
  } => {
    if (course.activeFlag == 0 || course.courseActiveFlag == 0) {
      return {
        rowColor: 'inactive',
        rowStatus: 'Course Inactive',
      }
    }

    if (course.cancelFlag == 1) {
      return {
        rowColor: 'danger',
        rowStatus: 'Course Cancelled',
      }
    }


    const registration = this.userRegistrations?.find((registration: any) => {
      return registration.courseItemId == course.courseItemId;
    });

    if (registration) {
      const status = registration.currentStatusKid;

      const capitalCaseStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

      return {
        rowColor: 'success',
        rowStatus: capitalCaseStatus,
      }
    }

    return {
      rowColor: 'default',
      rowStatus: '',
    }
  }

  formatCourses = (courses: any) => {
    this.totalCount = courses?.pagination?.totalItems;
    this.courseList = courses?.rows;

    const formattedCourses = courses?.rows?.map((course: any) =>
      this.formatSingleCourse(course));

    return formattedCourses;
  }

  formatMeta = (courses: any): NewTableData['meta'] => {
    const { pagination } = courses;

    return {
      usePagination: true,

      curPage: pagination.curPage,
      itemsPerPage: pagination.totalPerPage,
      totalItems: pagination.totalItems,
      totalPages: pagination.totalPages,
      links: courses.links,
    }
  };

  // #endregion Format Courses

  // #region Table Event Handling
  onRowClicked(itemId: TableRowClickEvent) {
    const selectedCourse = this.courseList.find((course: any) => course.courseItemId == itemId);

    this.openCourseDetailsModal(selectedCourse);
  }

  onSortClicked(event: TableSortEvent) {
    const { columnName, sortDirection } = event;

    console.log('Course catalog received sort event:', { columnName, sortDirection });

    // Ensure we have valid sort parameters
    if (!columnName || columnName === 'undefined') {
      console.error('Invalid columnName received:', columnName);
      return;
    }

    // Ensure sortDirection is valid, default to ASC if undefined
    const validSortDirection = sortDirection && ['ASC', 'DESC'].includes(sortDirection) ? sortDirection : 'ASC';

    this.sortColumn = columnName;
    this.sortDirection = validSortDirection;

    console.log('Sort parameters set:', { sortColumn: this.sortColumn, sortDirection: this.sortDirection });

    this.searchCourses();
  }

  onPageChanged(event: TablePageChangeEvent) {
    const offset = (event - 1) * this.limit;
    this.offset = offset;
    this.searchCourses();
  }

  onItemsPerPageChange(event: number) {
    this.limit = event;
    this.offset = 0;
    this.searchCourses();
  }

  onBurgerMenuClicked(event: TableBurgerClickEvent) {
    const { eventName, itemId } = event;
    const selectedCourse = this.courseList.find((course: any) => course.courseItemId === itemId);

    if (eventName === 'register' || eventName === 'joinWaitlist') {
      // Check if user is already registered for this course
      if (this.checkForRegistration(itemId)) {
        this.toastService.setToast({
          text: 'You are already registered for this course',
          type: 'info',
          icon: true,
        });
        return;
      }
      this.openRegistrationModal(selectedCourse);
    }

    if (eventName === 'viewDetails') {
      this.openCourseDetailsModal(selectedCourse);
    }

    if (eventName === 'editCourse') {
      this.openUpdateModal(selectedCourse);
    }

    if (eventName === 'cancelCourse') {
      this.cancelCourse(selectedCourse.courseItemId);
    }
  }

  // #endregion Table Event Handling

  // #region Table Meatball Menu Handling
  getCourse = (courseItemId: number) => {
    return this.courseList.find((courseItem: any) => courseItem.courseItemId === courseItemId);
  }

  checkCourseCancelled = (courseItemId: number) => {
    const course = this.courseList.find((courseItem: any) => {
      return courseItem.courseItemId === courseItemId;
    });

    return course.cancelFlag == 1 || course.courseActiveFlag == 0;
  }

  checkForRegistration = (courseItemId: number) => {
    const isRegistered = this.userCurrentRegistrationIds.includes(courseItemId);

    return isRegistered;
  }

  confirmCourseStatus = (courseItemId: number) => {
    const course = this.courseList.find((courseItem: any) => {
      return courseItem.courseItemId === courseItemId;
    });

    const {
      registeredCount, courseSize, allowWaitListFlag,
    } = course;

    const cancelled = course.cancelFlag == 1;


    return {
      canWaitlist: (allowWaitListFlag == 1 && registeredCount >= courseSize) && !cancelled,
      classFull: registeredCount >= courseSize,
    };

  }

  courseIsCancelled = (courseItemId: number) => {
    const course = this.courseList.find((courseItem: any) => {
      return courseItem.courseItemId === courseItemId;
    });

    return course.cancelFlag == 1;
  }

  confirmPermissions = () => {
    return this.userCanEdit || this.userCanCreate;
  }

  cancelCourse = (courseItemId: number) => {
    const foundCourse = this.courseList.find((courseItem: any) => {
      return courseItem.courseItemId == courseItemId;
    })
    const confirmDialogRef = this.dialog.open(VerificationComponent, {
      data: {
        type: 'alert',
        title: 'Are you sure you want to cancel this session?',
        text: 'Users will be unregistered and the session will be removed. ',
        confirmButtonTitle: 'Yes, cancel course',
        cancelButtonTitle: 'No, go back',
      },
    })
    confirmDialogRef.afterClosed().subscribe((result: any) => {
      if (result === 'verified') {
        // TODO: this needs to be updated to single sessions once a course has multiple sessions
        const sub = this.coursesService.cancelCourse(foundCourse.courseId, confirmDialogRef);
        // const sub = this.coursesService.cancelCourseSession(courseItemId, confirmDialogRef);

        sub.add(() => {
          this.searchCourses();
        })
      }
    })
  }
  // #endregion Table Meatball Menu Handling

  // #region Filter Functions
  updateYearFilter = (yearId: any) => {
    this.formatFilter(this.mainFilter);
    this.yearId = yearId;
    this.searchCourses();
  }

  setMeatball = (event: Event, meatballMenu: any) => {
    event.stopPropagation();
    if (this.meatballMenu === meatballMenu) {
      this.meatballMenu = '';
      return;
    }
    this.meatballMenu = meatballMenu;
  }


  formatFilter = (filter: CourseFilter) => {
    const requirementIds = filter.requirements?.map((requirement: any) => { return parseFloat(requirement.id) });
    const requirementIdString = requirementIds?.join(', ');

    const tagIds = filter.tags?.map((tag: any) => { return parseFloat(tag.id) });
    const tagIdString = tagIds?.join(', ');

    const categoryIds = filter.categories?.map((category: any) => { return parseFloat(category.id) });
    const categoryIdString = categoryIds?.join(', ');

    const courseTypeIds = filter.courseTypes?.map((courseType: any) => { return parseFloat(courseType.id) });
    const courseTypeIdString = courseTypeIds?.join(', ');

    const buildingIds = filter.buildings?.map((building: any) => { return parseFloat(building.id) });
    const buildingIdString = buildingIds?.join(', ');


    // TODO: need to add the time range filter, once they are available in the api
    const formattedFilters: any = {
      includedAssociations: 'Tags, Items',
      showCancelled: filter.showCancelledCoursesFlag ? 1 : 0,
      showAsync: filter.showAsyncFlag ? 1 : 0,
      showAnytime: filter.showAnytimeCoursesFlag ? 1 : 0,
      showVirtual: filter.showVirtualFlag ? 1 : 0,
      showOutsideCourse: filter.showOutsideCoursesFlag ? 1 : 0,
      showWaitlisted: filter.showWaitlistedCoursesFlag ? 1 : 0,
      ignoreRegistrationWindow: filter.showUserViewFlag ? 0 : 1,
      showInactive: filter.showInactiveCoursesFlag ? 1 : 0,
      specialRequestFlag: filter.specialRequestFlag ? 1 : 0,
      courseNameFilter: filter.courseNameFilter || null,
      targetValueMin: filter.hours,

      requirementIds: requirementIdString || null,
      tagIds: tagIdString || null,
      categoryIds: categoryIdString || null,
      buildingIds: buildingIdString || null,
      courseTypeIds: courseTypeIdString || null,

      startDateRange: formatDate.formatStartDateRange(filter?.startDate),
      endDateRange: formatDate.formatEndDateRange(filter?.endDate),
    };
    // this removes null and empty string values from formatted filters
    Object.keys(formattedFilters).forEach((key) => (formattedFilters[key] === null || formattedFilters[key] === '') && delete formattedFilters[key]);

    this.currentFilters = formattedFilters;

    return formattedFilters
  }

  searchCoursesByString = (name: string) => {
    this.filterString = name;
    this.searchCourses();
  }

  // #endregion Filter Functions

  // #region Modal Handling
  popAddNewModal = () => {
    const dialog = this.dialog.open(UpdateCourseComponent, { disableClose: true })

    dialog.afterClosed().subscribe(() => {
      this.isLoading = true;
      this.formatFilter(this.mainFilter);
      this.searchCourses();
    });
  }

  openUpdateModal = (selectedCourse: any) => {
    const dialog = this.dialog.open(UpdateCourseComponent, { data: selectedCourse, disableClose: true })

    dialog.afterClosed().subscribe(result => {
      if (result?.statusCode === 1000) {
        this.isLoading = true;
        this.formatFilter(this.mainFilter);
        this.searchCourses();
        this.userCurrentRegistrationIds.push(result.courseItemId);
      }
      this.searchForUserRegistrations();
    });
  }

  openFiltersModal = () => {
    const dialogRef = this.dialog.open(CourseFiltersComponent, { data: this.mainFilter })
    dialogRef.afterClosed().subscribe(result => {
      if (result && !result.noChange) {
        this.isLoading = true;

        // TODO: need to add the time range filter, hours filter, buildings filter, categories filter, and course types filter once they are available in the api
        this.formatFilter(result.filter);

        this.offset = 0;

        this.searchCourses();
      } else if (result && result.noChange) {
        this.isLoading = false;
      }
    })
  }

  openCourseDetailsModal = (selectedCourse: any) => {
    this.meatballMenu = '';
    const dialogRef = this.dialog.open(CatalogCourseDetailsComponent, { data: { selectedCourse, userRequirements: this.user.Requirements } })

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'reload' || result?.statusCode === 1000) {
        this.isLoading = true;
        this.formatFilter(this.mainFilter);
        this.searchForUserRegistrations();
        this.searchCourses();
      }
    })
  }

  openRegistrationModal = (course: any) => {
    this.meatballMenu = '';
    const selectedCourse = this.courseList.find((courseItem: any) => courseItem.courseItemId === course.courseItemId);
    const dialog = this.dialog.open(RegistrationRequirementModalComponent, { data: { selectedCourse, userRequirements: this.user.Requirements } })

    dialog.afterClosed().subscribe(result => {
      if (result === 'reload') {
        this.isLoading = true;
        this.formatFilter(this.mainFilter);
        this.searchForUserRegistrations();
        this.searchCourses();
      }
    });
  }

  openWaitlistModal = (event: Event, course: any) => {
    event.stopPropagation();
    this.meatballMenu = '';
    const selectedCourse = this.courseList.find((courseItem: any) => courseItem.courseItemId === course.courseItemId);
    const dialog = this.dialog.open(RegistrationWaitlistModalComponent, { data: { selectedCourse } })

    dialog.afterClosed().subscribe(result => {
      if (result === 'reload') {
        this.isLoading = true;
        this.formatFilter(this.mainFilter);
        this.searchForUserRegistrations();
        this.searchCourses();
      }
    });
  }

  //   searchCourses = (event?: any) => {
  // TODO: Course Catalog Filter by string endpoint really slow and needs to be optimized





  getCourseCallFilters = (exportFlag: boolean) => {
    this.formatFilter(this.mainFilter);

    const randomUUID = Math.random().toString(36).substring(2, 15);
    this.currentRequestId = randomUUID;

    // Ensure sort parameters are valid before sending to API
    const validSortColumn = this.sortColumn && this.sortColumn !== 'undefined' ? this.sortColumn : 'dateOffered';
    const validSortDirection = this.sortDirection && ['ASC', 'DESC'].includes(this.sortDirection) ? this.sortDirection : 'ASC';

    console.log('API call with sort parameters:', { sortColumn: validSortColumn, sortDirection: validSortDirection });

    const args = exportFlag ? {
      requestId: randomUUID,
      includedAssociations: 'Tags, Items, CourseType, Requirements, StateRequirement',
      courseNameFilter: this.filterString,
      limit: this.limit,
      offset: this.offset,
      sortColumn: validSortColumn,
      sortDirection: validSortDirection,
      ...this.currentFilters,
    } : {
      requestId: randomUUID,
      includedAssociations: 'Tags, Items, StateRequirement',
      courseNameFilter: this.filterString,
      limit: this.limit,
      offset: this.offset,
      sortColumn: validSortColumn,
      sortDirection: validSortDirection,
      ...this.currentFilters,
    };

    if (this.yearId) {
      args.yearId = this.yearId;

      if (this.yearId !== this.curYearId && this.hasPrivilege) {
        args.ignoreRegistrationWindow = 1;
      }
    }

    return args;
  }

  exportCourses = () => {
    this.loadingSpinnerService.setIsLoading(true);

    this.exportCoursesDataSubscription = this.exportCoursesDataSubscription ? this.exportCoursesDataSubscription : this.coursesService.exportCoursesData$.subscribe((data: any) => {
      if (!data || data.error) {
        console.log('No export data available or error occurred');
        return;
      }

      const exportCourseList = data.rows || [];
      console.log('exportCourseList', exportCourseList);

      if (exportCourseList.length === 0) {
        console.log('No courses to export');
        return;
      }

      // Generate PDF export
      this.generatePDFFromExportData(exportCourseList); //this  should set spinner to false
      this.loadingSpinnerService.setIsLoading(false); //failsafe for setting to false
    })
    const args = this.getCourseCallFilters(true);

    // Call the export service to trigger the subscription
    this.coursesService.getCourseSessionsForExport(args);
  }

  generatePDFFromExportData = (exportCourseList: any[]) => {
    // Create a temporary container for the PDF content
    const pdfContainer = document.createElement('div');
    pdfContainer.style.cssText = `
      font-family: Arial, sans-serif;
      color: #000;
      background: white;
      padding: 20px;
      max-width: 800px;
      margin: 0 auto;
    `;

    // Add header with Kalpa logo
    const header = document.createElement('div');
    header.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; border-bottom: 2px solid #e25b34; padding-bottom: 15px;">
        <div style="display: flex; align-items: center; gap: 16px;">
          <svg width="150" height="90" viewBox="0 0 300 90" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_1560_845)">
              <path d="M24.0845 71.9812C25.656 65.4892 27.8116 59.2015 30.7019 53.1755C34.291 45.6927 38.8692 38.8836 44.5817 32.8485C50.3264 26.7795 56.8954 21.8017 64.2976 17.9312C68.1663 15.9082 72.1639 14.2077 76.3551 13.009C68.2594 4.96885 57.1086 0 44.7967 0C20.0564 0 0 20.0564 0 44.7967C0 61.1905 8.80883 75.5255 21.9504 83.3344C22.4772 79.5231 23.176 75.7369 24.0863 71.9812H24.0845ZM15.3653 27.8743C15.3563 27.7973 15.3151 27.722 15.2882 27.6467V25.3836C15.3706 24.9321 15.4477 24.4805 15.5355 24.0308C16.4368 19.4597 20.1334 15.7917 24.5737 15.0589C29.6626 14.2203 34.5813 16.8722 36.5917 21.6942C38.4625 26.1774 37.9052 30.4421 34.7766 34.1781C29.8687 40.0411 20.8054 39.1738 16.9296 32.5618C16.0766 31.1068 15.557 29.5443 15.3635 27.8707L15.3653 27.8743Z" fill="#E25B34"/>
              <path d="M66.3493 68.625C63.1365 64.3693 59.2356 60.9558 54.2721 58.8736C50.407 57.252 46.3646 56.641 42.1913 56.8811C40.9531 56.9528 39.7203 57.123 38.4839 57.2377C38.2116 57.2627 38.079 57.381 37.9912 57.6462C36.5075 62.1044 35.3786 66.6504 34.5669 71.2787C33.7229 76.0863 33.2176 80.9315 33.0976 85.8072C33.0797 86.5508 33.0779 87.2963 33.085 88.0417C36.8193 89.0505 40.7435 89.5916 44.7967 89.5916C55.2935 89.5916 64.9445 85.981 72.5814 79.9335C70.9741 75.9125 68.9744 72.0994 66.3493 68.6232V68.625Z" fill="#E25B34"/>
              <path d="M77.8011 14.5088C75.8247 15.3993 73.8232 16.2433 71.8897 17.2198C65.3691 20.5169 59.5473 24.7887 54.4495 30.0299C49.8802 34.7282 46.1119 40.007 43.0604 45.8019C42.5784 46.7158 42.1286 47.6458 41.6663 48.5686C41.6125 48.6779 41.5695 48.7926 41.5086 48.9341C41.6448 48.9574 41.7505 48.9879 41.858 48.9933C46.3072 49.2334 50.4733 50.4949 54.4172 52.5232C59.6584 55.22 63.907 59.0761 67.3957 63.7941C70.6354 68.1734 73.0114 72.9703 74.832 78.0305C83.897 69.8327 89.5934 57.9795 89.5934 44.7949C89.5934 33.1155 85.1227 22.4808 77.8011 14.507V14.5088Z" fill="#E25B34"/>
            </g>
            <defs>
              <clipPath id="clip0_1560_845">
                <rect width="300" height="89.5934" fill="white"/>
              </clipPath>
            </defs>
          </svg>
          <h1 style="color: #2e3b63; margin: 0; font-size: 24px; font-weight: 700;">
            Course Detail Report
          </h1>
        </div>
        <div style="text-align: right;">
          <p style="color: #797b80; margin: 0; font-size: 14px;">
            Generated on ${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })}
          </p>
        </div>
      </div>
    `;
    pdfContainer.appendChild(header);

    // Add course details
    exportCourseList.forEach((course, index) => {
      const courseSection = document.createElement('div');
      courseSection.style.cssText = `
        margin-bottom: 30px;
        border: 1px solid #d2d3d4;
        border-radius: 8px;
        padding: 20px;
        background: #f8f9fa;
      `;

      // Course title
      const courseTitle = document.createElement('h2');
      courseTitle.style.cssText = `
        color: #2e3b63;
        margin: 0 0 15px 0;
        font-size: 18px;
        border-bottom: 1px solid #e25b34;
        padding-bottom: 8px;
      `;
      courseTitle.textContent = course.courseName || 'Untitled Course';
      courseSection.appendChild(courseTitle);

      // Create grid for course details
      const detailsGrid = document.createElement('div');
      detailsGrid.style.cssText = `
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0px 36px;
        font-size: 14px;
      `;

      // Define the fields to display
      const fields = [
        { label: 'Course ID', value: course.courseId || 'N/A' },
        { label: 'Location', value: course.location || 'N/A' },
        { label: 'Date', value: course.date ? new Date(course.date).toLocaleDateString('en-US') : 'N/A' },
        {
          label: 'Time',
          value: course.sessionStartTime && course.sessionEndTime ?
            `${new Date(course.sessionStartTime).toLocaleTimeString('en-US', {
              hour: 'numeric', minute: '2-digit', hour12: true,
            })} - ${new Date(course.sessionEndTime).toLocaleTimeString('en-US', {
              hour: 'numeric', minute: '2-digit', hour12: true,
            })}` : 'N/A',
        },
        { label: 'Hours', value: course.hours || '0' },
        { label: 'Active', value: course.active || 'No' },
        { label: 'Target Audience', value: course.targetAudience || 'N/A' },
        { label: 'Course Type', value: course.courseType || 'N/A' },
        { label: 'Short Name', value: course.shortName || course.courseName || 'N/A' },
        { label: 'Instructor', value: course.instructor || 'N/A' },
        { label: 'Effective Date', value: course.effectiveDate || 'N/A' },
        { label: 'Short Location', value: course.shortLocation || course.location || 'N/A' },
        { label: 'Expiration Date', value: course.expirationDate || 'N/A' },
        { label: 'Course Size', value: course.courseSize || '0' },
        { label: 'Freeze Date', value: course.freezeDate || 'N/A' },
        { label: 'Cost', value: `$${course.cost || '0.00'}` },
        { label: 'Outside Course', value: course.outsideCourse || 'No' },
      ];

      // Add fields to grid
      fields.forEach(field => {
        const fieldDiv = document.createElement('div');
        fieldDiv.style.cssText = `
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #e8e9e9;
        `;

        const label = document.createElement('span');
        label.style.cssText = `
          font-weight: bold;
          color: #2e3b63;
          min-width: 120px;
        `;
        label.textContent = field.label;

        const value = document.createElement('span');
        value.style.cssText = `
          color: #505255;
          text-align: right;
        `;
        value.textContent = field.value;

        fieldDiv.appendChild(label);
        fieldDiv.appendChild(value);
        detailsGrid.appendChild(fieldDiv);
      });

      courseSection.appendChild(detailsGrid);

      // Add description if available
      if (course.description) {
        const descriptionDiv = document.createElement('div');
        descriptionDiv.style.cssText = `
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid #e8e9e9;
        `;

        const descriptionLabel = document.createElement('h3');
        descriptionLabel.style.cssText = `
          color: #2e3b63;
          margin: 0 0 8px 0;
          font-size: 16px;
        `;
        descriptionLabel.textContent = 'Description';

        const descriptionText = document.createElement('div');
        descriptionText.style.cssText = `
          color: #505255;
          margin: 0;
          line-height: 1.5;
          font-size: 14px;
        `;
        descriptionText.innerHTML = course.description;

        descriptionDiv.appendChild(descriptionLabel);
        descriptionDiv.appendChild(descriptionText);
        courseSection.appendChild(descriptionDiv);
      }

      // Add requirements if available
      if (course.requirements && course.requirements.length > 0) {
        const requirementsDiv = document.createElement('div');
        requirementsDiv.style.cssText = `
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid #e8e9e9;
        `;

        const requirementsLabel = document.createElement('h3');
        requirementsLabel.style.cssText = `
          color: #2e3b63;
          margin: 0 0 8px 0;
          font-size: 16px;
        `;
        requirementsLabel.textContent = 'Requirements';

        const requirementsList = document.createElement('ul');
        requirementsList.style.cssText = `
          color: #505255;
          margin: 0;
          padding-left: 20px;
          font-size: 14px;
        `;

        const requirementsArray = Array.isArray(course.requirements) ? course.requirements : [course.requirements];
        requirementsArray.forEach(req => {
          const listItem = document.createElement('li');
          listItem.textContent = req;
          requirementsList.appendChild(listItem);
        });

        requirementsDiv.appendChild(requirementsLabel);
        requirementsDiv.appendChild(requirementsList);
        courseSection.appendChild(requirementsDiv);
      }

      pdfContainer.appendChild(courseSection);
    });

    // Add to document temporarily
    document.body.appendChild(pdfContainer);

    // Generate PDF using browser print functionality
    this.loadingSpinnerService.setIsLoading(false);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Course Detail Report</title>
          <style>
            @media print {
              .no-print { display: none; }
            }
            body { 
              margin: 0; 
              padding: 0;
            }
            div {
              border: none !important;
              outline: none !important;
            }

          </style>
        </head>
        <body>
        <div style="
          text-align: center;
          padding: 20px 0;
          margin-bottom: 20px;
        ">
        </br>
          <button onclick="window.print()" style="
            background: #2e3b63;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          ">Print PDF</button>
          <div style="
            width: 800px;
            height: 2px;
            background-color: #e25b34;
            max-width: 100%;
            margin: 0 auto;
            margin-top: 20px;
          "></div>
        </div>
          ${pdfContainer.outerHTML}
          <div class="no-print" style="text-align: center; margin-top: 20px;">
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();

    // Clean up
    document.body.removeChild(pdfContainer);
  }

  exportButtonText = () => {
    return this.isExportDisabled ? 'Export is limited to 500 records or fewer' : 'Export a PDF courses in the catalog';
  }


  // #endregion Modal Handling

  ngOnDestroy(): void {
    this?.coursesDataSubscription?.unsubscribe();
    this?.exportCoursesDataSubscription?.unsubscribe();
    this?.entitySubscription?.unsubscribe();
    this?.userSubscription?.unsubscribe();
    this?.userWithRequirementsSubscription?.unsubscribe();
    this?.permissionSubscription?.unsubscribe();
  }
}
